import { trimBoundarySlashes } from 'utils/String';

import { escape } from './Regex';

type Vars = Record<string, string>;
type Formatter = (vars: Vars) => string;

function literalFormatter(literal: string) {
    return (_: Vars) => literal;
}

function varFormatter(varName: string) {
    return (vars: Vars) => {
        const v = vars[varName];
        if (v === undefined) {
            const qVarName = JSON.stringify(varName);
            throw new Error(`PathTemplate: missing var ${qVarName}`);
        }
        return v;
    };
}

// Uses leading-colon syntax to match router. Bikeshed away
function parseTemplate(template: string) {
    const tokens = template.split(/:([A-Za-z_][0-9A-Za-z_]*)/);

    let regex = '^';
    const groupNames = [];
    const formatters: Formatter[] = [];

    for (let i = 0; i < tokens.length; i += 2) {
        const literal = tokens[i];
        regex += escape(literal);
        formatters.push(literalFormatter(literal));
        if (i + 1 < tokens.length) {
            const varName = tokens[i + 1];
            regex += '([^/]+)';
            groupNames.push(varName);
            formatters.push(varFormatter(varName));
        }
    }

    return {
        regex: new RegExp(regex + '$'),
        groupNames,
        formatters,
    };
}

export class PathTemplate {
    private readonly regex: RegExp;
    private readonly groupNames: ReadonlyArray<string>;
    private readonly formatters: ReadonlyArray<Formatter>;

    constructor(public readonly template: string) {
        const { regex, groupNames, formatters } = parseTemplate(template);
        this.regex = regex;
        this.groupNames = groupNames;
        this.formatters = formatters;
    }

    public match(path: string): Record<string, string> | null {
        const match = this.regex.exec(trimBoundarySlashes(path));
        return match
            ? Object.fromEntries(
                  this.groupNames.map((gn, index0) => [gn, match[index0 + 1]])
              )
            : null;
    }

    public requireMatch(path: string): Record<string, string> {
        const result = this.match(path);
        if (result === null) {
            const qPath = JSON.stringify(path);
            const qTemplate = JSON.stringify(this.template);
            throw new Error(
                `PathTemplate: ${qPath} did not match template ${qTemplate}`
            );
        }
        return result;
    }

    public fill(vars: Record<string, string>): string {
        return this.formatters.map((f) => f(vars)).join('');
    }
}
